import { Anchor, Badge, Box, Button, Card, Group, Stack, Text, ThemeIcon, Timeline, Title, Tooltip } from '@mantine/core';
import { IconEyeOff, IconHistory } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { WORKFLOW_EVENT_LABELS, type ClientHistoryLineDto, type RequestHistoryDto, type RequestHistoryEntryDto } from '@mexp/shared';
import { http } from '@/api/client';
import { ErrorState, LoadingState } from '@/components/States';
import { FileList } from '@/features/files/FileList';
import { formatBusinessDate, formatDateTime, fromNow } from '@/lib/format';
import { EVENT_ICONS } from './EventTimeline';

/** Under the `dr` key, so request and task changes (and the live socket) refresh it. */
export const historyKey = (requestId: string) => ['dr', 'history', requestId] as const;

const FIRST = 12;

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()) : []);

/** A labelled line in an entry: "RA" (what happened, in full) or "Client" (what the client sees). */
function Side({ label, color, children }: { label: string; color: string; children: ReactNode }) {
  return (
    <Group gap={8} wrap="nowrap" align="flex-start">
      <Badge size="xs" variant="light" color={color} w={58} style={{ flex: 'none', marginTop: 2, overflow: 'visible' }}>
        {label}
      </Badge>
      <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
        {children}
      </Stack>
    </Group>
  );
}

function Documents({ names, label }: { names: string[]; label: string }) {
  if (!names.length) return null;
  return (
    <Group gap={4}>
      <Text fz="xs" c="dimmed">
        {label}
      </Text>
      {names.map((d) => (
        <Badge key={d} size="xs" variant="outline" color="gray" tt="none">
          {d}
        </Badge>
      ))}
    </Group>
  );
}

const quoted = (text: string) => (
  <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
    “{text}”
  </Text>
);

/** Everything staff should know about an event: the message and the specifics stored with it. */
function RaLine({ e }: { e: RequestHistoryEntryDto }) {
  const d = e.data;
  const reason = str(d.reason);
  // The client's query and RA's compliance already carry the text in the message.
  const comment = ['client_changes_requested', 'query_compliance_sent', 'clarification_answered'].includes(e.type) ? null : str(d.comment);
  const note = str(d.note) ?? str(d.notes);
  const round = typeof d.round === 'number' && ['item_submitted', 'item_resubmitted', 'item_verified', 'item_returned', 'item_reopened'].includes(e.type) ? d.round : null;
  const facts = [
    round ? `${e.queryTask ? 'Compliance' : 'Version'} ${round}` : null,
    e.type === 'item_added' && str(d.department) ? `Department ${d.department}` : null,
    (e.type === 'item_added' || e.type === 'target_accepted') && str(d.targetDate ?? d.date) ? `Target ${formatBusinessDate(str(d.targetDate ?? d.date))}` : null,
    e.type === 'target_proposed' && str(d.date) ? `Proposed ${formatBusinessDate(str(d.date))}` : null,
    e.type === 'target_rejected' && str(d.proposed) ? `Proposed ${formatBusinessDate(str(d.proposed))}` : null,
    e.type === 'request_updated' && str(d.requiredBy) ? `Needed by ${formatBusinessDate(str(d.requiredBy))}` : null,
    typeof d.version === 'number' ? `Version ${d.version}` : null,
  ].filter(Boolean);
  return (
    <Side label="RA" color="gray">
      {e.message && (
        <Text fz="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
          {e.message}
        </Text>
      )}
      {facts.length > 0 && (
        <Text fz="xs" c="dimmed">
          {facts.join(' · ')}
        </Text>
      )}
      {reason && quoted(reason)}
      {comment && quoted(comment)}
      <Documents names={strings(d.documents)} label="Documents to change:" />
      {note && !reason && (
        <Text fz="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
          Note: {note}
        </Text>
      )}
      {e.files.length > 0 && <FileList files={e.files} />}
      {str(d.link) && (
        <Anchor href={str(d.link)!} target="_blank" rel="noopener noreferrer" fz="sm">
          Shared folder
        </Anchor>
      )}
    </Side>
  );
}

/** Exactly what the client's portal history shows for the event, or that it is not shown to them. */
function ClientLine({ line }: { line: ClientHistoryLineDto | null }) {
  return (
    <Side label="Client" color="teal">
      {line ? (
        <>
          <Text fz="sm" fw={500}>
            {line.label}
          </Text>
          {line.detail &&
            (/^(by|via) /.test(line.detail) ? (
              <Text fz="xs" c="dimmed">
                {line.detail}
              </Text>
            ) : (
              quoted(line.detail)
            ))}
          <Documents names={line.documents} label="Documents:" />
          {line.files.length > 0 && <FileList files={line.files} />}
        </>
      ) : (
        <Group gap={4} wrap="nowrap">
          <IconEyeOff size={14} color="var(--mantine-color-dimmed)" style={{ flex: 'none' }} />
          <Text fz="xs" c="dimmed">
            Not shown to the client
          </Text>
        </Group>
      )}
    </Side>
  );
}

/** The history as a timeline, newest first, the latest 12 until expanded. */
function HistoryList({ data }: { data: RequestHistoryDto }) {
  const [all, setAll] = useState(false);
  const items = [...data.items].reverse();
  const shown = all ? items : items.slice(0, FIRST);
  if (!items.length) {
    return (
      <Text fz="sm" c="dimmed">
        Nothing has happened yet.
      </Text>
    );
  }
  return (
    <Stack gap="sm">
      <Timeline bulletSize={26} lineWidth={2}>
        {shown.map((e) => {
          const meta = EVENT_ICONS[e.type] ?? { icon: IconHistory, color: 'gray' };
          return (
            <Timeline.Item
              key={e.id}
              bullet={
                <ThemeIcon size={26} radius="xl" color={meta.color} variant="light">
                  <meta.icon size={15} />
                </ThemeIcon>
              }
              title={
                <Group gap={6} wrap="wrap">
                  <Text fz="sm" fw={600}>
                    {WORKFLOW_EVENT_LABELS[e.type]}
                  </Text>
                  {e.workItemCode && (
                    <Badge size="xs" variant="outline" color="gray" ff="monospace">
                      {e.workItemCode}
                    </Badge>
                  )}
                </Group>
              }
            >
              <Stack gap={8} mt={4}>
                <RaLine e={e} />
                {data.clientVisible && <ClientLine line={e.client} />}
                <Tooltip label={formatDateTime(e.createdAt)}>
                  <Text fz="xs" c="dimmed" w="fit-content">
                    {e.actor?.name ?? 'System'} · {fromNow(e.createdAt)}
                  </Text>
                </Tooltip>
              </Stack>
            </Timeline.Item>
          );
        })}
      </Timeline>
      {items.length > FIRST && (
        <Box>
          <Button variant="subtle" size="compact-sm" onClick={() => setAll((v) => !v)}>
            {all ? 'Show the latest only' : `Show all ${items.length} events`}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

/**
 * The request's history for staff (right column of the request page). Each event shows what happened in full (RA)
 * and, for portal requisitions, what the client's portal history shows for it (Client).
 */
export function RequestHistory({ requestId }: { requestId: string }) {
  const query = useQuery({ queryKey: historyKey(requestId), queryFn: () => http.get<RequestHistoryDto>(`/dossier-requests/${requestId}/history`) });
  const count = query.data?.items.length ?? 0;
  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Title order={4}>History</Title>
          {count > 0 && (
            <Text fz="xs" c="dimmed" className="tabular">
              {count} event{count === 1 ? '' : 's'}
            </Text>
          )}
        </Group>
        {query.data && !query.data.clientVisible && (
          <Text fz="xs" c="dimmed">
            Raised outside the client portal: the client does not see this request.
          </Text>
        )}
        {query.isLoading ? (
          <LoadingState />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          query.data && <HistoryList data={query.data} />
        )}
      </Stack>
    </Card>
  );
}

/** One task's history (task panel). Coordinators also see the client line; assignees see what happened only. */
export function TaskHistory({ taskId }: { taskId: string }) {
  const query = useQuery({
    queryKey: ['dr', 'task-history', taskId] as const,
    queryFn: () => http.get<RequestHistoryDto>(`/work-items/${taskId}/history`),
    enabled: !!taskId,
  });
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return query.data ? <HistoryList data={query.data} /> : null;
}
